import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  ChevronRight,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/NewButton';
import { useDockets } from '@/hooks/useDockets';
import { apiErrorMessage } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import DocketDocumentLinkDialog from './DocketDocumentLinkDialog';
import { formatDocketDate, isAwaitingFirstSync } from './format';
import type { Docket, DocketDocument, LinkedCrimsonDocument } from './schema';
import { StatusPill } from './StatusPill';

interface DocketEntriesProps {
  docket: Docket;
  matterGuid: string;
  // Entry guid to reveal and briefly highlight (e.g. from a Case Partner citation).
  highlightEntryGuid?: string | null;
  // Bumped on each deep-link so a second click of the same guid re-scrolls.
  highlightToken?: number;
}

type SortDirection = 'asc' | 'desc';

type DocumentOpenTarget =
  | { href: string; kind: 'file' }
  | { href: string; kind: 'courtlistener' }
  | { href: string; kind: 'pacer' };

// Entry numbers are strings ("1", "12", "204") but are conceptually numeric.
// Used only to break ties between filings on the same day.
const entryNumberValue = (entryNumber: string): number => {
  const parsed = Number.parseFloat(entryNumber);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const filedOnValue = (filedOn: string | null | undefined): string => filedOn ?? '';

const documentOpenTarget = (doc: DocketDocument): DocumentOpenTarget | null => {
  // Prefer the filing itself: public PDF, then PACER, then the provider page.
  if (doc.downloadUrl) return { href: doc.downloadUrl, kind: 'file' };
  if (doc.pacerUrl) return { href: doc.pacerUrl, kind: 'pacer' };
  if (doc.sourceUrl) return { href: doc.sourceUrl, kind: 'courtlistener' };
  return null;
};

const DocumentExternalLink = ({
  target,
  onPacerClick,
}: {
  target: DocumentOpenTarget | null;
  onPacerClick: (href: string) => void;
}) => {
  if (!target) return <span className="size-6 shrink-0" aria-hidden="true" />;
  if (target.kind === 'pacer') {
    return (
      <Button
        variant="ghost"
        size="xs"
        icon={ExternalLink}
        onClick={() => onPacerClick(target.href)}
        aria-label="Open on PACER (may incur fees)"
        tooltip="Open on PACER (may incur fees)"
        className="text-muted-foreground"
      />
    );
  }
  const isPublicFile = target.kind === 'file';
  const label = isPublicFile ? 'Open file' : 'Open on CourtListener';
  return (
    <Button
      variant="ghost"
      size="xs"
      icon={isPublicFile ? FileText : ExternalLink}
      href={target.href}
      target="_blank"
      aria-label={label}
      tooltip={label}
      className="text-muted-foreground"
    />
  );
};

const DocketEntries = ({
  docket,
  matterGuid,
  highlightEntryGuid,
  highlightToken,
}: DocketEntriesProps) => {
  const { linkDocument, unlinkDocument } = useDockets(matterGuid);
  const [linkTarget, setLinkTarget] = useState<DocketDocument | null>(null);
  const [pendingPacerUrl, setPendingPacerUrl] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [highlightedGuid, setHighlightedGuid] = useState<string | null>(null);
  const entriesRef = useRef(docket.entries);
  entriesRef.current = docket.entries;

  // Reveal and briefly highlight a deep-linked entry. Depend only on the
  // citation identity: the page waits until the guid is in the list before
  // setting highlightEntryGuid, and list refreshes must not re-scroll.
  useEffect(() => {
    if (!highlightEntryGuid) {
      setHighlightedGuid(null);
      return;
    }
    if (!entriesRef.current.some((entry) => entry.guid === highlightEntryGuid)) return;

    const element = document.getElementById(`docket-entry-${highlightEntryGuid}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedGuid(highlightEntryGuid);
    const timer = window.setTimeout(() => setHighlightedGuid(null), 2400);
    return () => window.clearTimeout(timer);
  }, [highlightEntryGuid, highlightToken]);

  // The server is the source of truth for the linked Crimson file.
  const resolveLink = (doc: DocketDocument): LinkedCrimsonDocument | null =>
    doc.linkedDocument ?? null;

  const entries = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...docket.entries].sort((a, b) => {
      // Undated filings stay at the bottom in either direction.
      const aMissing = !a.filedOn;
      const bMissing = !b.filedOn;
      if (aMissing || bMissing) {
        if (aMissing && bMissing) {
          return (entryNumberValue(a.entryNumber) - entryNumberValue(b.entryNumber)) * direction;
        }
        return aMissing ? 1 : -1;
      }
      const byDate = filedOnValue(a.filedOn).localeCompare(filedOnValue(b.filedOn));
      if (byDate !== 0) return byDate * direction;
      return (entryNumberValue(a.entryNumber) - entryNumberValue(b.entryNumber)) * direction;
    });
  }, [docket.entries, sortDirection]);

  const handleLink = async (file: LinkedCrimsonDocument) => {
    if (!linkTarget) return;
    const target = linkTarget;
    try {
      await linkDocument(docket.id, target.guid, file.guid);
      toast.success(`Linked ${file.name} to "${target.title}"`);
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Could not link this file. Please try again.');
    }
  };

  const handleUnlink = async (doc: DocketDocument) => {
    try {
      await unlinkDocument(docket.id, doc.guid);
      toast.success(`Unlinked file from "${doc.title}"`);
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? 'Could not unlink this file. Please try again.');
    }
  };

  const openPacer = () => {
    if (!pendingPacerUrl) return;
    window.open(pendingPacerUrl, '_blank', 'noopener,noreferrer');
    setPendingPacerUrl(null);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 p-6 md:py-8">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Entries</CardTitle>
          {entries.length > 0 && (
            <Badge variant="secondary" className="font-medium text-muted-foreground">
              {entries.length}
            </Badge>
          )}
        </div>
        {entries.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            icon={sortDirection === 'asc' ? ArrowUpNarrowWide : ArrowDownNarrowWide}
            onClick={() =>
              setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
            }
            tooltip={`Sort by docket number (${sortDirection === 'asc' ? 'ascending' : 'descending'})`}
            className="text-muted-foreground"
          />
        )}
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          isAwaitingFirstSync(docket) ? (
            <p className="text-sm text-muted-foreground">
              This docket was just added. Its entries are still being fetched and may take a few
              minutes to appear.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No entries on this docket.</p>
          )
        ) : (
          <ol className="flex flex-col gap-4">
            {entries.map((entry) => (
              <li
                key={entry.guid}
                id={`docket-entry-${entry.guid}`}
                className={cn(
                  'rounded-xl border border-border/60 bg-muted/40 px-4 py-4 transition-shadow',
                  highlightedGuid === entry.guid &&
                    'ring-2 ring-primary ring-offset-2 ring-offset-background',
                )}
              >
                <Collapsible>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] leading-none text-muted-foreground">
                    <span className="font-mono tabular-nums text-muted-foreground/80">
                      {entry.entryNumber}
                    </span>
                    <span aria-hidden="true" className="text-border">
                      ·
                    </span>
                    <span>{formatDocketDate(entry.filedOn)}</span>
                    {entry.documents.length > 0 && (
                      <CollapsibleTrigger className="group ml-0.5 inline-flex items-center gap-0.5 rounded-sm text-[11px] font-medium text-muted-foreground/80 transition-colors hover:text-foreground">
                        {entry.documents.length} file
                        {entry.documents.length === 1 ? '' : 's'}
                        <ChevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
                      </CollapsibleTrigger>
                    )}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground">
                    {entry.description}
                  </p>
                  {entry.documents.length > 0 && (
                    <CollapsibleContent>
                      <ul className="mt-3.5 overflow-hidden rounded-lg border border-border/50">
                        {entry.documents.map((doc) => {
                          const linkedFile = resolveLink(doc);
                          const label = doc.attachmentNumber
                            ? `${doc.title} · att. ${doc.attachmentNumber}`
                            : doc.title;
                          const openTarget = documentOpenTarget(doc);
                          return (
                            <li
                              key={doc.guid}
                              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 bg-background px-3 py-2.5 not-last:border-b not-last:border-border/50 transition-colors hover:bg-muted/30"
                            >
                              <div className="flex min-w-0 items-center gap-3 py-0.5 text-sm text-foreground/90">
                                <span className="min-w-0 flex-1 truncate">{label}</span>
                                <button
                                  type="button"
                                  onClick={() => setLinkTarget(doc)}
                                  aria-label={
                                    linkedFile
                                      ? `Change file linked to ${label}`
                                      : `Link a file to ${label}`
                                  }
                                  title={
                                    linkedFile
                                      ? `Linked to ${linkedFile.name} — click to change`
                                      : 'Link a file'
                                  }
                                  className="max-w-44 shrink-0 rounded-full"
                                >
                                  <StatusPill
                                    label={linkedFile ? linkedFile.name : 'Unlinked'}
                                    tone={linkedFile ? 'emerald' : 'slate'}
                                    className="max-w-44"
                                  />
                                </button>
                              </div>
                              <DocumentExternalLink
                                target={openTarget}
                                onPacerClick={setPendingPacerUrl}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </li>
            ))}
          </ol>
        )}
      </CardContent>

      <DocketDocumentLinkDialog
        open={linkTarget !== null}
        onOpenChange={(open) => {
          if (!open) setLinkTarget(null);
        }}
        matterGuid={matterGuid}
        document={linkTarget}
        currentLink={linkTarget ? resolveLink(linkTarget) : null}
        onLink={handleLink}
        onUnlink={() => {
          if (linkTarget) handleUnlink(linkTarget);
        }}
      />

      <AlertDialog
        open={pendingPacerUrl !== null}
        onOpenChange={(open) => {
          if (!open) setPendingPacerUrl(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open this document on PACER?</AlertDialogTitle>
            <AlertDialogDescription>
              PACER may charge a per-page fee for this document. Continue only if you have a PACER
              account and accept any charges.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={openPacer}>Continue to PACER</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DocketEntries;
